import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import { useRecoilValue } from 'recoil';
import DynamicTable from '../DynamicTable';
import PermissionsService, {
  PERMISSION_GDPR_PROFILE_LIST,
} from '../../Services/PermissionsService';
import { IDynamicTableAddButton } from '../../interfaces/tables';
import { TABLE_GDPR_PROFILES, TABLE_GDPR_PROFILES_HISTORY } from '../../Services/TableService';
import { URL_GDPR_PROFILE } from '../../router/constants';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { ITab } from '../TabComponent/interfaces';
import GdprProfileForm from './Form';
import { selectedCustomersAtom } from '../../store/recoil/customers';

const GDPR_URL_ADD = '/gdpr-profile/add';
const GDPR_PROFILE_HISTORY_URL = '/gdpr-profile/history';

export const tabsByPermission = (tabs: string[], active: string): ITab[] => {
  const enabledTabs: ITab[] = [];
  tabs.forEach((tabItem: string) => {
    if (
      tabItem === TABLE_GDPR_PROFILES &&
      PermissionsService.hasAccess(PERMISSION_GDPR_PROFILE_LIST, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: URL_GDPR_PROFILE,
      });
    }
    if (
      tabItem === TABLE_GDPR_PROFILES_HISTORY &&
      PermissionsService.hasAccess(PERMISSION_GDPR_PROFILE_LIST, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: GDPR_PROFILE_HISTORY_URL,
      });
    }
  });
  return enabledTabs;
};

export default function GdprProfile() {
  const { formatMessage: f } = useIntl();
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const [callTable, setCallTable] = React.useState<boolean>(false);
  const [itemId, setItemId] = React.useState<number | null>(null);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [usersCount, setUserscount] = React.useState<number | undefined>(undefined);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(
    `${customerId ? `${URL_GDPR_PROFILE}/edit/:cid/:id/:count` : `${URL_GDPR_PROFILE}/detail/:id`}`
  );
  const tabs = React.useState(
    tabsByPermission([TABLE_GDPR_PROFILES, TABLE_GDPR_PROFILES_HISTORY], TABLE_GDPR_PROFILES)
  )[0];
  const { setDocumentTitle } = useDocumentTitle();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);

  const handleClose = React.useCallback(() => {
    setIsSubpageOpened(false);
    setItemId(null);
    setUserscount(0);
    navigate(URL_GDPR_PROFILE);
  }, [navigate]);

  const updateTable = () => {
    setCallTable(true);
  };

  const handleForm = (
    id: number,
    isEdited: boolean | string,
    customerValue?: number,
    count?: number
  ) => {
    if (!isSubpageOpened) {
      setIsSubpageOpened(true);
      setCallTable(false);
      setItemId(isEdited && id ? id : null);
      setCustomerId(customerValue || null);
      if (count) {
        setUserscount(!Number.isNaN(Number(count)) ? count : 1);
      } else {
        setUserscount(0);
      }
    }
  };

  const buttons: IDynamicTableAddButton = {
    [TABLE_GDPR_PROFILES]: {
      open: ['gdprProfile.addProfile'],
    },
  };

  React.useEffect(() => {
    if (
      isSubpageOpened &&
      !pathname.includes('/add') &&
      !pathname.includes('/edit') &&
      !pathname.includes('/detail')
    ) {
      if (itemId) {
        const url = customerId ? `edit/${customerId}/${itemId}/${usersCount}` : `detail/${itemId}`;
        navigate(`${URL_GDPR_PROFILE}/${url}`);
      } else {
        navigate(`${URL_GDPR_PROFILE}/add`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubpageOpened]);

  React.useEffect(() => {
    if (pathname === URL_GDPR_PROFILE && isSubpageOpened) {
      setIsSubpageOpened(false);
      setCallTable(true);
      const documentTitle = `${f({ id: 'menu.gdprProfile' })} - ${f({ id: 'menu.gdpr' })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === GDPR_URL_ADD && !isSubpageOpened) {
      setIsSubpageOpened(true);
      setCallTable(false);
      const documentTitle = `${f({ id: 'table.add' })} - ${f({ id: 'menu.gdprProfile' })} - ${f({
        id: 'menu.gdpr',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (!isSubpageOpened) {
      const pathArr = pathname.split('/');
      if (pathArr[pathArr.length - 4] === 'edit') {
        setIsSubpageOpened(true);
        setItemId(Number(pathArr[pathArr.length - 2]));
        setCustomerId((oldState: number | null) => {
          if (pathArr[pathArr.length - 3] === 'NaN' || pathArr[pathArr.length - 3] === 'null') {
            return null;
          }
          return Number(pathArr[pathArr.length - 3]);
        });
        setUserscount(Number(pathArr[pathArr.length - 1]));
        setCallTable(false);
      } else if (pathArr[pathArr.length - 2] === 'detail') {
        setIsSubpageOpened(true);
        setItemId(Number(pathArr[pathArr.length - 1]));
        setCallTable(false);
      }
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({ id: 'menu.gdprProfile' })} - ${f({
        id: 'menu.gdpr',
      })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isCreatingAllowed = PermissionsService.hasAccess(
    PERMISSION_GDPR_PROFILE_LIST,
    ['create'],
    selectedCustomers
  );

  const isUpdatingAllowed = PermissionsService.hasAccess(
    PERMISSION_GDPR_PROFILE_LIST,
    ['update'],
    customerId || null
  );

  return (
    <div>
      <div
        className="col-12"
        style={{
          display: isSubpageOpened ? 'none' : '',
        }}
      >
        <div className="row section page-head">
          <h4>
            {`${f({ id: 'gdprProfile.mainTitle' })} / ${f({ id: 'menu.gdprProfile' })}`}
            <OnlineHelp topic="gdpr-profile" />
          </h4>
        </div>
        <TabComponent tabs={tabs}>
          <div>
            <DynamicTable
              name={TABLE_GDPR_PROFILES}
              mustClosed={true}
              updateTable={callTable}
              buttons={isCreatingAllowed ? buttons[TABLE_GDPR_PROFILES] : { open: [] }}
              handleForm={handleForm}
              actionsInMenu={true}
              routerParams={params}
            />
          </div>
        </TabComponent>
      </div>
      {isCreatingAllowed && pathname === `${URL_GDPR_PROFILE}/add` && (
        <div>
          <GdprProfileForm
            closeCallback={handleClose}
            editingItemId={itemId}
            updateTable={updateTable}
            customerId={customerId}
            usersCount={usersCount}
            isEdited={false}
          />
        </div>
      )}
      {(pathname === `${URL_GDPR_PROFILE}/edit/${customerId}/${itemId}/${usersCount}` ||
        pathname === `${URL_GDPR_PROFILE}/detail/${itemId}`) && (
        <div>
          <GdprProfileForm
            isEdited={isUpdatingAllowed}
            closeCallback={handleClose}
            editingItemId={itemId}
            updateTable={updateTable}
            customerId={customerId}
            usersCount={usersCount}
          />
        </div>
      )}
    </div>
  );
}
